import type { PanelMention } from './panel-mentions';
import { findPanelMentions, MENTION_TRIGGER } from './panel-mentions';

/**
 * The composer's document, and its two conversions to plain text.
 *
 * A mention is a node that holds the panel id, so the id is stored rather than derived. Reading the
 * ids back is a walk over the document, and no longer a scan of the sentence.
 *
 * The wire format does not change: a message is still the plain text a textarea would have produced,
 * with each mention written as `@Label`. That keeps stored history readable by the transcript, which
 * still matches labels because a message sent last year contains no nodes.
 */

/** The node name. Declared once so the schema, the walkers, and the suggestion plugin agree. */
export const PANEL_MENTION_NODE = 'panelMention';

/** A TipTap/ProseMirror JSON node, narrowed to the parts this module reads. */
export interface DocNode {
  type?: string;
  text?: string;
  attrs?: Record<string, unknown>;
  content?: DocNode[];
}

/** The plain text a document serializes to. This is what the message carries on the wire. */
export function docToText(doc: DocNode | null | undefined): string {
  if (!doc?.content) return '';
  return doc.content.map(blockToText).join('\n');
}

function blockToText(node: DocNode): string {
  if (node.type === 'paragraph') return (node.content ?? []).map(inlineToText).join('');
  return inlineToText(node);
}

function inlineToText(node: DocNode): string {
  if (node.type === 'text') return node.text ?? '';
  if (node.type === 'hardBreak') return '\n';
  if (node.type === PANEL_MENTION_NODE) return `${MENTION_TRIGGER}${readLabel(node)}`;
  return (node.content ?? []).map(inlineToText).join('');
}

function readLabel(node: DocNode): string {
  const label = node.attrs?.['label'];
  return typeof label === 'string' ? label : '';
}

function readId(node: DocNode): string | null {
  const id = node.attrs?.['id'];
  return typeof id === 'string' && id.length > 0 ? id : null;
}

/**
 * The panel ids a document names, first occurrence first, each counted once.
 *
 * Nothing is parsed. A mention the user deleted is not in the document, so it cannot be reported,
 * and a mention the user edited cannot exist — the node has no editable interior.
 */
export function readMentionIds(doc: DocNode | null | undefined): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();

  const visit = (node: DocNode): void => {
    if (node.type === PANEL_MENTION_NODE) {
      const id = readId(node);
      if (id && !seen.has(id)) {
        seen.add(id);
        ids.push(id);
      }
      return;
    }
    for (const child of node.content ?? []) visit(child);
  };

  if (doc) visit(doc);
  return ids;
}

/**
 * Rebuilds a document from plain text, turning every recognised `@Label` back into a node.
 *
 * This is the one place label matching survives, and it is deserialization rather than resolution:
 * an aborted prompt comes back from the server as a string, and its mentions must become nodes again
 * before the user can edit them.
 */
export function textToDoc(text: string, mentions: PanelMention[]): DocNode {
  const paragraphs = text.split('\n').map((line) => ({
    type: 'paragraph',
    content: lineToInline(line, mentions),
  }));
  return { type: 'doc', content: paragraphs.length > 0 ? paragraphs : [{ type: 'paragraph' }] };
}

function lineToInline(line: string, mentions: PanelMention[]): DocNode[] {
  const spans = findPanelMentions(line, mentions);
  const nodes: DocNode[] = [];
  let cursor = 0;

  for (const { start, end, mention } of spans) {
    if (start > cursor) nodes.push({ type: 'text', text: line.slice(cursor, start) });
    nodes.push({ type: PANEL_MENTION_NODE, attrs: { id: mention.id, label: mention.label } });
    cursor = end;
  }
  if (cursor < line.length) nodes.push({ type: 'text', text: line.slice(cursor) });

  return nodes;
}

/**
 * True when the document has no words of the user's own. Drives the send button's enabled state.
 *
 * A mention serializes to `@Label`, so a draft of nothing but panel names looks non-empty when you
 * read the serialized form. It names two panels and asks nothing, and the ticket says such a message
 * cannot be sent — so this counts the text nodes alone and ignores the mentions.
 */
export function isDocEmpty(doc: DocNode | null | undefined): boolean {
  return textNodesOnly(doc).trim().length === 0;
}

function textNodesOnly(node: DocNode | null | undefined): string {
  if (!node) return '';
  if (node.type === PANEL_MENTION_NODE) return '';
  if (node.type === 'text') return node.text ?? '';
  if (node.type === 'hardBreak') return '\n';
  return (node.content ?? []).map(textNodesOnly).join('');
}
